import { createClient } from "@supabase/supabase-js";
import { supabaseConfig } from "./supabaseConfig";

// console.log(`🔌 Supabase: Production mode`)
// console.log(`📡 Connecting to: ${supabaseConfig.url}`)

export const supabase = createClient(supabaseConfig.url, supabaseConfig.key, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false, // Disable automatic email redirect handling
  },
});

// Helper functions for affiliate data
export const affiliateService = {
  // Get all affiliates
  async getAffiliates() {
    const { data, error } = await supabase
      .from("affiliate_performance_summary")
      .select("*")
      .order("total_ad_spend", { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get individual ad performance for an affiliate
  async getAffiliateAds(affiliateId) {
    const { data, error } = await supabase
      .from("ad_performance")
      .select("*")
      .eq("affiliate_id", affiliateId)
      .order("ad_spend", { ascending: false });

    if (error) throw error;
    return data;
  },

  // Insert CSV data into the database
  async insertCSVData(csvData) {
    // First, get or create affiliates
    const affiliateMap = new Map();

    // Get existing affiliates
    const { data: existingAffiliates } = await supabase
      .from("affiliates")
      .select("id, name");

    existingAffiliates?.forEach((affiliate) => {
      affiliateMap.set(affiliate.name, affiliate.id);
    });

    // Process CSV data
    const processedData = [];

    for (const row of csvData) {
      if (!row["Ad Name"] || !row["Ad Spend"]) continue;

      // Extract affiliate name using your existing logic
      const affiliateName = extractAffiliateName(row["Ad Name"]);
      let affiliateId = affiliateMap.get(affiliateName);

      // Create affiliate if it doesn't exist
      if (!affiliateId) {
        const { data: newAffiliate, error } = await supabase
          .from("affiliates")
          .insert({ name: affiliateName })
          .select()
          .single();

        if (error && error.code !== "23505") {
          // Ignore duplicate key errors
          console.error("Error creating affiliate:", error);
          continue;
        }

        if (newAffiliate) {
          affiliateId = newAffiliate.id;
          affiliateMap.set(affiliateName, affiliateId);
        }
      }

      processedData.push({
        date: row["Date"] || new Date().toISOString().split("T")[0],
        affiliate_id: affiliateId,
        ad_name: row["Ad Name"],
        adset_name: row["Adset Name"],
        campaign_name: row["Campaign Name"],
        ad_spend: parseFloat(row["Ad Spend"]?.replace(/[$,]/g, "") || 0),
        pixel_cv: parseFloat(row["Pixel CV"]?.replace(/[$,]/g, "") || 0),
        roas: parseFloat(row["ROAS"] || row["Pixel ROAS"] || 0),
        pixel_roas: parseFloat(row["Pixel ROAS"] || 0),
        pixel_purchases: parseInt(row["Pixel Purchases"] || 0),
        pixel_cpa: parseFloat(row["Pixel CPA"]?.replace(/[$,]/g, "") || 0),
        pixel_aov: parseFloat(row["Pixel AOV"]?.replace(/[$,]/g, "") || 0),
      });
    }

    // Insert the processed data
    const { data, error } = await supabase
      .from("ad_performance")
      .insert(processedData);

    if (error) throw error;
    return data;
  },

  // Clear all ad performance data (for testing)
  async clearData() {
    const { error } = await supabase
      .from("ad_performance")
      .delete()
      .neq("id", 0); // Delete all records

    if (error) throw error;
  },
};

// Helper function to extract affiliate name (same logic as your helpers.js)
function extractAffiliateName(adName) {
  if (!adName) return "Hercules Team";

  const adNameLower = adName.toLowerCase();

  if (adNameLower.includes("ddiad")) return "Dillon";
  if (adNameLower.includes("hhorg")) return "Dillon";
  if (adNameLower.includes("dnah")) return "Leaks";
  if (adNameLower.includes("jsotto")) return "Jemuel";
  if (adNameLower.includes("jor")) return "jor";
  if (adNameLower.includes("gray")) return "Gray";
  if (adNameLower.includes("ori")) return "Ori";
  if (adNameLower.includes("zane")) return "Zane";
  if (adNameLower.includes("munsur")) return "Munsur";
  if (adNameLower.startsWith("dd")) return "Dillon";
  if (adNameLower.startsWith("t")) return "Tarun";

  // Check whitelisted affiliates
  const whitelistedAffiliates = [
    "Dillon",
    "Tarun",
    "Leaks",
    "Jemuel",
    "Gray",
    "jor",
    "kam111",
    "TasDigital",
    "Ori",
    "Zane",
    "Munsur",
    "Kiana",
    "Maai",
  ];
  for (const name of whitelistedAffiliates) {
    if (adNameLower.includes(name.toLowerCase())) return name;
  }

  return "Hercules Team";
}
